/**
 * OPButtons' help articles, bundled into the app (2026-10-05).
 *
 * The articles live in the repo's own `help/` folder, one Markdown file per
 * answer (OPApps/CONTRACTS/op-report.md section 4, OPButtons' proposal). The
 * help site builds its pages from the SAME files, so the portal and this
 * in-app view always say the same thing. Bundling them here is what makes help
 * open at a venue with no internet: they ride in the app bundle the service
 * worker already caches.
 *
 * Front matter is plain `key: value` lines between `---` fences: title, slug,
 * words (or its synonym asks), summary, section, order.
 *
 * DEV GOTCHA (measured 2026-10-05): the dev server caches this glob and does
 * not watch `help/` (it is outside the workspace) for NEW files, so a new
 * article shows nothing until this file is touched or the server restarts.
 * Edits to an existing article reload normally. Production builds are not
 * affected.
 */

const RAW = import.meta.glob('../../../../../help/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;

export type HelpSection = 'getting-started' | 'how-to' | 'troubleshooting' | 'reference';

export const SECTION_TITLES: Record<HelpSection, string> = {
  'getting-started': 'Getting started',
  'how-to': 'How do I...',
  troubleshooting: 'When something goes wrong',
  reference: 'Reference',
};
const SECTION_ORDER: HelpSection[] = ['getting-started', 'how-to', 'troubleshooting', 'reference'];

export interface HelpArticle {
  slug: string;
  title: string;
  summary: string;
  section: HelpSection;
  order: number;
  words: string[];
  body: string;
}

export function parseArticle(text: string, fallbackSlug: string): HelpArticle {
  const src = text.replace(/\r\n/g, '\n');
  const meta: Record<string, string> = {};
  let body = src;
  const m = /^---\n([\s\S]*?)\n---\n?/.exec(src);
  if (m) {
    body = src.slice(m[0].length);
    for (const line of m[1]!.split('\n')) {
      const i = line.indexOf(':');
      if (i > 0) meta[line.slice(0, i).trim().toLowerCase()] = line.slice(i + 1).trim();
    }
  }
  const section = (SECTION_ORDER as string[]).includes(meta.section ?? '') ? (meta.section as HelpSection) : 'how-to';
  return {
    slug: meta.slug || fallbackSlug,
    title: meta.title || fallbackSlug,
    summary: meta.summary || '',
    section,
    order: Number.isFinite(Number(meta.order)) ? Number(meta.order) : 999,
    words: (meta.words || meta.asks || '').split(',').map((w) => w.trim().toLowerCase()).filter(Boolean),
    body: body.trim(),
  };
}

const ALL: HelpArticle[] = Object.entries(RAW)
  .map(([path, text]) => parseArticle(text, path.split('/').pop()!.replace(/\.md$/, '')))
  .sort((a, b) => SECTION_ORDER.indexOf(a.section) - SECTION_ORDER.indexOf(b.section) || a.order - b.order || a.title.localeCompare(b.title));

/** Every article, in reading order. What's new is listed separately. */
export const HELP_ARTICLES: HelpArticle[] = ALL.filter((a) => a.slug !== 'whats-new');
export const WHATS_NEW: HelpArticle | null = ALL.find((a) => a.slug === 'whats-new') ?? null;

export function helpArticle(slug: string): HelpArticle | null {
  return ALL.find((a) => a.slug === slug) ?? null;
}

export function articlesIn(section: HelpSection): HelpArticle[] {
  return HELP_ARTICLES.filter((a) => a.section === section);
}

export const HELP_SECTIONS: HelpSection[] = SECTION_ORDER;

/** The public address of an answer: what "Open on the web" and other apps use. */
export function helpUrl(slug: string): string {
  return `https://support.overpoweredapps.net/opbuttons/${encodeURIComponent(slug)}`;
}

/**
 * Search in the person's own words. Every word typed must appear somewhere in
 * the article; matches in the title and the listed search words rank above a
 * match buried in the body.
 */
export function searchHelp(query: string): HelpArticle[] {
  const terms = query.toLowerCase().split(/\s+/).filter((t) => t.length > 1);
  if (terms.length === 0) return [];
  const scored: { a: HelpArticle; score: number }[] = [];
  for (const a of ALL) {
    const title = a.title.toLowerCase();
    const words = a.words.join(' ');
    const summary = a.summary.toLowerCase();
    const body = a.body.toLowerCase();
    let score = 0;
    let all = true;
    for (const t of terms) {
      const s = (title.includes(t) ? 6 : 0) + (words.includes(t) ? 4 : 0) + (summary.includes(t) ? 2 : 0) + (body.includes(t) ? 1 : 0);
      if (s === 0) { all = false; break; }
      score += s;
    }
    if (all) scored.push({ a, score });
  }
  return scored.sort((x, y) => y.score - x.score || x.a.order - y.a.order).map((s) => s.a);
}
